import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

interface NumpadInputPanelProps {
    placeholder?: string;
    onChange?: (value: string) => void;
    onSubmit?: (value: string) => void;
}

/**
 * Numpad input panel extracted from MathGridActivity and MultiModalInputArea.
 *
 * Provides a large-button number pad suitable for young children on tablets.
 */
export const NumpadInputPanel: React.FC<NumpadInputPanelProps> = ({
    placeholder = 'Enter your answer...',
    onChange,
    onSubmit,
}) => {
    const [typedValue, setTypedValue] = useState('');

    const update = (next: string) => {
        setTypedValue(next);
        onChange?.(next);
    };

    const handlePress = (digit: string) => update(typedValue + digit);
    const handleBackspace = () => update(typedValue.slice(0, -1));
    const handleClear = () => update('');

    const handleSubmit = () => {
        if (typedValue.trim()) {
            onSubmit?.(typedValue.trim());
        }
    };

    return (
        <View style={styles.panel}>
            <View style={styles.display}>
                <Text style={styles.displayText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{typedValue || placeholder}</Text>
            </View>
            <View style={styles.grid}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '←'].map((key) => (
                    <TouchableOpacity
                        key={key}
                        testID={key === 'C' ? 'numpad-clear' : key === '←' ? 'numpad-backspace' : `numpad-key-${key}`}
                        style={styles.key}
                        onPress={() => {
                            if (key === 'C') handleClear();
                            else if (key === '←') handleBackspace();
                            else handlePress(key);
                        }}
                        activeOpacity={0.6}
                        accessibilityRole="button"
                        accessibilityLabel={key === 'C' ? 'Clear' : key === '←' ? 'Backspace' : `Number ${key}`}
                    >
                        <Text style={styles.keyText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{key}</Text>
                    </TouchableOpacity>
                ))}
            </View>
            <TouchableOpacity testID="numpad-submit" style={styles.submitBtn} onPress={handleSubmit} activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Submit"
                accessibilityHint="Submits your answer"
            >
                <Text style={styles.submitBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Submit</Text>
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    panel: {
        // flexGrow + flexBasis 'auto' (not flex:1): fills bounded parents but
        // sizes to content inside indefinite-height containers, where a
        // zero-basis flex would collapse to 0 height under Yoga.
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        padding: 16,
        justifyContent: 'center',
    },
    display: {
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
        alignItems: 'center',
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    displayText: {
        fontSize: 32,
        fontWeight: '800',
        color: '#f1f5f9',
        minHeight: 40,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 12,
    },
    key: {
        width: '28%',
        aspectRatio: 1,
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    keyText: {
        fontSize: 28,
        fontWeight: '800',
        color: '#f1f5f9',
    },
    submitBtn: {
        marginTop: 16,
        backgroundColor: '#6366f1',
        borderRadius: 16,
        paddingVertical: 16,
        alignItems: 'center',
    },
    submitBtnText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: '800',
    },
});
